import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { ACCOUNT_COOKIE_NAME, LOGIN_URL } from '../constants';
import { readCookie, writeCookie } from '@zapier/cookies';
import { getCurrentAccountCookieDomain } from '../utils/getCurrentAccountCookieDomain';

// A Date in the past used to expire (delete) a cookie via writeCookie.
// This matches the deletion idiom used elsewhere in the repo
// (see packages/authentication/src/hooks/useAuthType/utils.ts).
const EXPIRED_DATE = new Date(Date.now() - 10);

export function getAccountIdFromCookie() {
  if (typeof document === 'undefined') {
    return undefined;
  }
  // check if the currentAccountId cookie has been set more than once with differing values
  // if so, we need to expire all of them and force a reauth
  // https://zapierorg.atlassian.net/browse/TA-44
  const cookies = document.cookie.split(';');
  const currentAccountIdCookies = cookies.filter(cookie =>
    cookie.includes(ACCOUNT_COOKIE_NAME)
  );
  const allCookiesAreTheSame = currentAccountIdCookies.every(
    cookie => cookie.trim() === currentAccountIdCookies[0].trim()
  );

  // If there are multiple cookies with different values, we need to expire them and force a reauth
  if (currentAccountIdCookies.length > 1 && !allCookiesAreTheSame) {
    // Delete the host-only cookie (no domain attr) using the repo's cookie
    // abstraction so writes go through the same consent/serialization path.
    writeCookie(ACCOUNT_COOKIE_NAME, '', {
      category: 'essential',
      expires: EXPIRED_DATE,
      path: '/',
    });

    // Delete the shared-domain cookie using the registrable domain (.zapier.com in prod),
    // so the backend's Set-Cookie domain is actually cleared.  Previously this used
    // `.${window.location.hostname}` which resolves to `.admin.zapier.com` on a subdomain
    // host and therefore misses the `.zapier.com`-scoped cookie — the TA-44 loop root cause.
    const cookieDomain = getCurrentAccountCookieDomain();
    if (cookieDomain) {
      writeCookie(ACCOUNT_COOKIE_NAME, '', {
        category: 'essential',
        expires: EXPIRED_DATE,
        path: '/',
        domain: cookieDomain,
      });
    }

    const next = new URLSearchParams(window.location.search).get('next');
    window.location.href = addTruthyParamsToUrl(LOGIN_URL, {
      next,
      reauth: true,
    });
    return undefined;
  }
  // If there is only one cookie, we can return the account ID
  const accountId = readCookie(ACCOUNT_COOKIE_NAME);
  // If there is no cookie, we need to force a reauth
  if (!accountId) {
    return undefined;
  }
  // If the cookie is not a number, we need to force a reauth
  const parsedAccountId = parseInt(accountId, 10);
  if (isNaN(parsedAccountId)) {
    return undefined;
  }
  return parsedAccountId;
}
